import { useCommandPalette } from '@components/Common/CommandPalette/CommandPaletteProvider';
import {
  NAV_GROUP_ORDER,
  visibleNavItems as visibleNavItems_,
} from '@components/Common/Utils/NavigationItems';
import { routePath } from '@components/Common/Utils/Routes';
import { formatRole } from '@components/Common/Utils/roles';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { logoutUserProfileRequest } from '@store/features/loginUserProfile/actions';
import type { RootState } from '@store/rootReducer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import './index.scss';

/**
 * Phone navigation (< 768px).
 *
 * Two pieces, because they answer two different questions:
 *   - the bottom bar carries the five things a recruiter does away from a desk
 *   - the drawer carries everything else, in the same grouped structure as the
 *     desktop sidebar, so the mental model does not change between devices
 *
 * Bottom-bar tabs are chosen by frequency, not nav order.
 */

const BOTTOM_TABS = [
  { id: 'home',     label: 'Home',     icon: <DashboardOutlinedIcon />,     path: '/dashboard' },
  { id: 'queue',    label: 'Queue',    icon: <AppsIcon />,                  path: '/applications/list' },
  { id: 'pipeline', label: 'Pipeline', icon: <ViewKanbanOutlinedIcon />,    path: '/applications/board' },
  { id: 'alerts',   label: 'Alerts',   icon: <NotificationsOutlinedIcon />, path: '/notifications' },
];

/** How long to let a bar toggle settle before reading scroll direction again. Covers the 0.2s
 * transform transition plus a frame. */
const SETTLE_MS = 260;

const MobileNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { open: openPalette } = useCommandPalette();
  const profile = useSelector((state: RootState) => state.loginUserProfile.profile);

  const firstName = profile?.personalDetails?.firstName || '';
  const lastName = profile?.personalDetails?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [barHidden, setBarHidden] = useState(false);
  const lastScroll = useRef(0);
  // Which element `lastScroll` refers to. Two scrollers on one page (a list and the
  // drawer) would otherwise compare positions from different elements and flap the bar.
  const lastScrollTarget = useRef<HTMLElement | null>(null);
  /*
   * Ignore scroll events we caused ourselves.
   *
   * Hiding the bar grows the scroller by the bar's height, which lowers its maximum
   * scrollTop; at the foot of a list the browser clamps scrollTop to fit. That clamp is a
   * DECREASE, which reads here as an upward scroll, which shows the bar, which shrinks the
   * scroller again -- a loop that flickers the bar at the bottom of every long list.
   * Inside this window the handler only resyncs its reference point.
   */
  const settleUntil = useRef(0);

  const handleLogout = () => {
    dispatch(logoutUserProfileRequest());
    navigate(routePath('Login'));
    setDrawerOpen(false);
  };

  const visibleNavItems = visibleNavItems_(profile?.role);

  // Close the drawer whenever navigation happens, however it was triggered.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Lock the page behind an open drawer so the drawer scrolls, not the page.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  /**
   * The bar hides on scroll-down and returns on scroll-up, so a long list is
   * not permanently 62px shorter. The shell is position:fixed, so the window
   * never scrolls -- the scroller is always some element inside it.
   *
   * Listening in the CAPTURE phase on the document, rather than binding
   * `.main-content-container` directly, because which element scrolls depends
   * on the page. On a CRM list it is the body Box inside CRMPageTemplate, and
   * `.main-content-container` is exactly its own content height and never
   * scrolls at all -- so this listener heard nothing, the bar never hid, and
   * every long list was permanently 62px shorter. Precisely what the paragraph
   * above says must not happen. Scroll events do not bubble, but they are
   * dispatched through the capture phase, so one document-level listener sees
   * whichever element actually moved.
   */
  /*
   * Mirror the bar's state onto <body> so the stylesheet can shrink the space reserved for it.
   *
   * The reservation lives on `.main-content-container`, which MobileNav does not render, and a
   * fixed bar cannot tell its page how much room it is using. A body class is the seam already
   * used here for the drawer's scroll lock.
   */
  useEffect(() => {
    document.body.classList.toggle('cc-bar-hidden', barHidden);
    return () => document.body.classList.remove('cc-bar-hidden');
  }, [barHidden]);

  useEffect(() => {
    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      // The drawer has its own scroller and covers the bar while open; scrolling
      // it must not drive the bar underneath.
      if (target.closest('.cc-drawer')) return;

      const y = target.scrollTop;

      // A different element than last time: record where it stands, decide next move.
      if (target !== lastScrollTarget.current) {
        lastScrollTarget.current = target;
        lastScroll.current = y;
        return;
      }
      // A layout change of ours is still settling: take the new position as the reference and
      // decide nothing from it.
      if (performance.now() < settleUntil.current) {
        lastScroll.current = y;
        return;
      }
      // Ignore rubber-banding and sub-pixel jitter.
      if (Math.abs(y - lastScroll.current) < 8) return;

      const next = y > lastScroll.current && y > 60;
      setBarHidden((current) => {
        if (current !== next) settleUntil.current = performance.now() + SETTLE_MS;
        return next;
      });
      lastScroll.current = y;
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [location.pathname]);

  const isActive = useCallback(
    (path: string) => location.pathname.split('/')[1] === path.split('/')[1],
    [location.pathname],
  );

  return (
    <>
      {/* ── Header: menu + search. The sidebar is off-canvas here. ── */}
      <Box className="cc-mobile-header" component="header">
        <IconButton
          aria-label="Open navigation"
              size="large"
          className="cc-mobile-header__btn"
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon />
        </IconButton>
        <img alt="Mintly" className="cc-mobile-header__mark" src="/mintly-logo.png" />
        <Box className="cc-mobile-header__spacer" />
        <IconButton
          aria-label="Search"
              size="large"
          className="cc-mobile-header__btn"
          onClick={openPalette}
        >
          <SearchIcon />
        </IconButton>
      </Box>

      {/* ── Off-canvas drawer ── */}
      {drawerOpen && (
        <div
          className="cc-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          role="presentation"
        />
      )}
      <nav
        className={`cc-drawer${drawerOpen ? ' is-open' : ''}`}
        aria-label="Primary"
        aria-hidden={!drawerOpen}
      >
        <Box className="cc-drawer__header">
          <img alt="Mintly" className="cc-drawer__mark" src="/mintly-logo.png" />
          <Box className="cc-drawer__brand">
            <Typography component="span" className="cc-drawer__wordmark">Mintly</Typography>
            <Typography component="span" className="cc-drawer__subbrand">Control Center</Typography>
          </Box>
          <IconButton
            aria-label="Close navigation"
              size="large"
            className="cc-drawer__close"
            onClick={() => setDrawerOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="cc-drawer__nav">
          {NAV_GROUP_ORDER.map((group) => {
            const items = visibleNavItems.filter((i) => i.group === group);
            if (items.length === 0) return null;
            return (
              <Box key={group} className="cc-drawer__group">
                <Typography component="div" className="cc-drawer__group-label">{group}</Typography>
                {items.map(({ id, icon, label, path, roleTag }) => (
                  <button
                    key={id}
                    type="button"
                    className={`cc-drawer__row${isActive(path) ? ' is-active' : ''}`}
                    onClick={() => navigate(path)}
                    aria-current={isActive(path) ? 'page' : undefined}
                  >
                    <span className="cc-drawer__row-icon">{icon}</span>
                    <span className="cc-drawer__row-label">{label}</span>
                    {roleTag && <span className="cc-drawer__row-tag">{roleTag}</span>}
                  </button>
                ))}
              </Box>
            );
          })}
        </Box>

        {/*
          The account block the desktop sidebar carries in its footer. It has to
          be repeated here, not merely styled in: below 768px .cc-sidebar is
          display:none, so until this existed there was no way to see which
          account you were in — or to sign out of it — on a phone at all.
        */}
        <Box className="cc-drawer__account">
          <Avatar className="cc-drawer__avatar">{initials || 'U'}</Avatar>
          <Box className="cc-drawer__identity">
            <span className="cc-drawer__user-name">{fullName || 'User'}</span>
            <span className="cc-drawer__user-role">{formatRole(profile?.role)}</span>
          </Box>
          <Tooltip title="Your profile">
            <IconButton
              aria-label="Your profile"
              size="large"
              className="cc-drawer__account-btn"
              onClick={() => navigate('/user-details')}
            >
              <PersonOutlineIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sign out">
            <IconButton
              aria-label="Sign out"
              size="large"
              className="cc-drawer__account-btn cc-drawer__account-btn--exit"
              onClick={handleLogout}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </nav>

      {/* ── Bottom bar ── */}
      <Box className={`cc-bottombar${barHidden ? ' is-hidden' : ''}`} component="nav" aria-label="Quick navigation">
        {BOTTOM_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`cc-bottombar__tab${isActive(tab.path) ? ' is-active' : ''}`}
            onClick={() => navigate(tab.path)}
            aria-current={isActive(tab.path) ? 'page' : undefined}
          >
            <span className="cc-bottombar__icon">{tab.icon}</span>
            <span className="cc-bottombar__label">{tab.label}</span>
          </button>
        ))}
        <button
          type="button"
          className="cc-bottombar__tab"
          onClick={() => setDrawerOpen(true)}
          aria-label="More sections"
        >
          <span className="cc-bottombar__icon"><MoreHorizIcon /></span>
          <span className="cc-bottombar__label">More</span>
        </button>
      </Box>
    </>
  );
};

export default MobileNav;
